import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import toast from "react-hot-toast";
import HireTalentLayout from "../../layouts/HireTalentLayout";
import Loader from "../../components/loader";
import {
  fetchHireTalentApplicationProjects,
  fetchHireTalentJobApplications,
  rejectHireTalentApplicant,
  scheduleHireTalentInterview,
  sendHireTalentMassRejectionEmail,
  updateHireTalentQuickApplicantStatus,
} from "../../redux/features/hire-talent/actions/authAction";
import TableHeader from "../../components/table/tableHeader";
import { pipViewDate2 } from "../../utils/pip";
import { coachPaths } from "../../routes/coach";

const Applicants = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    subLoading,
    jobApplications,
    applicationProjects,
    selectedProject,
    selectedProjectId,
    selectedApplicationTab,
    applicationCounts,
  } = useSelector(
    (state) => state.hireTalent.auth,
  );
  const [activeApplicant, setActiveApplicant] = useState(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isQuickContactModalOpen, setIsQuickContactModalOpen] = useState(false);
  const [quickContactApplicant, setQuickContactApplicant] = useState(null);
  const [quickStatusFilter, setQuickStatusFilter] = useState("all");
  const [isSelectAllRejected, setIsSelectAllRejected] = useState(false);
  const [selectedRejectedIds, setSelectedRejectedIds] = useState([]);
  const [isMassRejectionModalOpen, setIsMassRejectionModalOpen] = useState(false);
  const [massMailSubject, setMassMailSubject] = useState("");
  const [massMailBody, setMassMailBody] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [activeProjectId, setActiveProjectId] = useState("");
  const [scheduleData, setScheduleData] = useState({
    date: "",
    time: "",
  });
  const quickStatusOptions = [
    { value: "new", label: "New" },
    { value: "shortlist", label: "ShortList" },
    { value: "contact", label: "Follow-up" },
    { value: "reject", label: "Reject" },
    { value: "remove", label: "Remove" },
  ];
  const quickFilterOptions = [
    { value: "all", label: "All Status" },
    { value: "new", label: "New" },
    { value: "shortlist", label: "ShortList" },
    { value: "contact", label: "Follow-up" },
    { value: "reject", label: "Rejected" },
    { value: "remove", label: "Removed" },
  ];

  const normalizeStatus = (statusValue) =>
    String(statusValue || "")
      .trim()
      .toLowerCase()
      .replace(/[_\s]+/g, "-");

  const getQuickStatusValue = (statusValue) => {
    const normalized = normalizeStatus(statusValue);
    if (normalized === "applied" || normalized === "new") return "new";
    if (normalized === "contact" || normalized === "follow-up") return "contact";
    if (normalized === "shortlist" || normalized === "shortlisted") return "shortlist";
    if (normalized === "reject" || normalized === "rejected") return "reject";
    if (normalized === "remove" || normalized === "removed" || normalized === "dismissed") return "remove";
    return "new";
  };

  const getQuickStatusLabel = (statusValue) => {
    const quickValue = getQuickStatusValue(statusValue);
    if (quickValue === "new") return "New";
    if (quickValue === "shortlist") return "Shortlisted";
    if (quickValue === "contact") return "Contacted";
    if (quickValue === "reject") return "Rejected";
    if (quickValue === "remove") return "Removed";
    return "New";
  };

  const isQuickStatusLocked = (statusValue) => {
    const normalized = normalizeStatus(statusValue);
    return ["rejected", "reject", "shortlisted", "shortlist"].includes(normalized);
  };

  const getApiActionFromQuickValue = (quickValue) => {
    if (quickValue === "new") return "new";
    if (quickValue === "contact") return "contact";
    if (quickValue === "shortlist") return "shortlist";
    if (quickValue === "reject") return "reject";
    if (quickValue === "remove") return "remove";
    return "new";
  };

  const normalizedQuickFilter =
    quickStatusFilter === "follow-up" ? "contact" : quickStatusFilter;
  const isQuickApplyTab = activeTab === "quick_apply";
  const quickFilteredApplications = jobApplications.filter((item) => {
    if (!isQuickApplyTab || normalizedQuickFilter === "all") return true;
    const itemQuickStatus = getQuickStatusValue(item?.status);
    return itemQuickStatus === normalizedQuickFilter;
  });
  const displayedApplications = isQuickApplyTab
    ? quickFilteredApplications
    : jobApplications;
  const isRejectedFilterActive =
    isQuickApplyTab && normalizedQuickFilter === "reject";

  const loadApplications = useCallback((projectId, tabValue) => {
    dispatch(
      fetchHireTalentJobApplications({
        project_id: projectId || undefined,
        tab: tabValue || "all",
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchHireTalentApplicationProjects());
  }, [dispatch]);

  useEffect(() => {
    if (selectedApplicationTab) {
      setActiveTab(selectedApplicationTab);
    }
  }, [selectedApplicationTab]);

  useEffect(() => {
    if (selectedProjectId) {
      setActiveProjectId(String(selectedProjectId));
      return;
    }
    if (!activeProjectId && applicationProjects?.length > 0) {
      const initialProjectId = String(applicationProjects[0]?.project_id || "");
      if (initialProjectId) {
        setActiveProjectId(initialProjectId);
        loadApplications(initialProjectId, activeTab);
      }
    }
  }, [applicationProjects, selectedProjectId, activeProjectId, activeTab, loadApplications]);

  useEffect(() => {
    if (!isRejectedFilterActive) {
      setIsSelectAllRejected(false);
      setSelectedRejectedIds([]);
      return;
    }
    const availableIds = quickFilteredApplications
      .map((item) => Number(item?.applied_id))
      .filter((id) => Number.isFinite(id));
    setSelectedRejectedIds((prev) =>
      prev.filter((id) => availableIds.includes(Number(id))),
    );
  }, [isRejectedFilterActive, quickFilteredApplications]);

  useEffect(() => {
    if (!isRejectedFilterActive) return;
    const availableIds = quickFilteredApplications
      .map((item) => Number(item?.applied_id))
      .filter((id) => Number.isFinite(id));
    const allSelected =
      availableIds.length > 0 &&
      availableIds.every((id) => selectedRejectedIds.includes(id));
    setIsSelectAllRejected(allSelected);
  }, [isRejectedFilterActive, quickFilteredApplications, selectedRejectedIds]);

  const handleScheduleChange = (e) => {
    const { name, value } = e.target;
    setScheduleData((prev) => ({ ...prev, [name]: value }));
  };

  const getTodayDateString = () => new Date().toISOString().split("T")[0];

  const getMinTimeForSelectedDate = () => {
    if (!scheduleData.date) return undefined;
    const today = getTodayDateString();
    if (scheduleData.date !== today) return undefined;
    const minDate = new Date();
    minDate.setMinutes(minDate.getMinutes() + 15);
    const hours = String(minDate.getHours()).padStart(2, "0");
    const minutes = String(minDate.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const tabLabelMap = {
    all: "All Applicants",
    quick_apply: "Quick Applicants",
    nuvio_profiles: "Nuvio Profiles",
    screened: "Screened",
    scheduled: "Scheduled",
    hired: "Hired",
    dismissed: "Dismissed",
  };

  const tabOrder = [
    "all",
    "nuvio_profiles",
    "screened",
    "scheduled",
    "hired",
    "dismissed",
    "quick_apply",
  ];

  const dynamicTabs = tabOrder;
  const isActiveApplicantQuickApply =
    Boolean(activeApplicant?.is_quick_apply) || isQuickApplyTab;
  const isQuickApplicantNewInModal =
    getQuickStatusValue(activeApplicant?.status) === "new";
  const applicantNameParts = String(activeApplicant?.full_name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const applicantFirstName = applicantNameParts[0] || "#N/A";
  const applicantLastName = applicantNameParts.slice(1).join(" ") || "#N/A";
  const applicantExperience = activeApplicant?.job_years_of_experience
    ? `${activeApplicant?.job_years_of_experience} Years`
    : activeApplicant?.years_of_experience || "#N/A";
  const applicantLocation = activeApplicant?.job_location || activeApplicant?.location || "#N/A";
  const applicantLinkedinUrl = activeApplicant?.linkedin_url
    ? /^https?:\/\//i.test(activeApplicant?.linkedin_url)
      ? activeApplicant?.linkedin_url
      : `https://${activeApplicant?.linkedin_url}`
    : "";
  const applicantPortfolioUrl = activeApplicant?.portfolio_url
    ? /^https?:\/\//i.test(activeApplicant?.portfolio_url)
      ? activeApplicant?.portfolio_url
      : `https://${activeApplicant?.portfolio_url}`
    : "";
  const applicantSkills = Array.isArray(activeApplicant?.skills)
    ? activeApplicant?.skills
    : String(
      activeApplicant?.skills ||
      activeApplicant?.key_skills ||
      activeApplicant?.job_skills ||
      "",
    )
      .split(",")
      .map((skill) => String(skill || "").trim())
      .filter(Boolean);
  const workOptions = [
    { label: "Remote", value: "Remote Freelance" },
    { label: "Full-time", value: "Full-time Freelance" },
    { label: "Part-time", value: "Part-time Freelance" },
    { label: "Hybrid", value: "Hybrid Freelance" },
    { label: "On-Site", value: "On-Site Freelance" },
    { label: "Project-Based", value: "Project Based" },
  ];
  const currentHireTalentProject = applicationProjects?.find(
    (project) =>
      String(project?.project_id || project?.job_id || "") ===
      String(activeProjectId || selectedProjectId || ""),
  );
  const currentProjectTitle =
    selectedProject?.project_title ||
    selectedProject?.job_title ||
    currentHireTalentProject?.project_title ||
    currentHireTalentProject?.job_title ||
    "N/A";
  const profileData = JSON.parse(localStorage.getItem("hire-talent_profile_nuvio"))?.company_name || "#N/A";
  const currentCompanyName =
    profileData ||
    "N/A";
  const noop = () => null;
  const tableHeaders = isQuickApplyTab
    ? [
      "Candidate Name",
      "Application date",
      "Location",
      "Availability",
      "Experience",
      "Status",
      "Action",
    ]
    : [
      "#",
      "Applicant",
      "Job Title",
      "Email",
      "Phone",
      "Expected Salary",
      "Work Type",
      "Status",
      "Applied At",
      "Action",
    ];

  const handleProjectChange = (event) => {
    const nextProjectId = event.target.value;
    setActiveProjectId(nextProjectId);
    loadApplications(nextProjectId, activeTab);
  };

  const handleTabChange = (tabValue) => {
    setActiveTab(tabValue);
    loadApplications(activeProjectId, tabValue);
  };

  const handleQuickStatusFilterChange = (event) => {
    const value = event.target.value;
    setQuickStatusFilter(value);
  };

  const handleSelectAllRejected = (event) => {
    const isChecked = event.target.checked;
    setIsSelectAllRejected(isChecked);
    if (!isChecked) {
      setSelectedRejectedIds([]);
      return;
    }
    const ids = quickFilteredApplications
      .map((item) => Number(item?.applied_id))
      .filter((id) => Number.isFinite(id));
    setSelectedRejectedIds(ids);
  };

  const handleOpenMassRejectionModal = () => {
    setMassMailSubject(``);
    setMassMailBody(
      ``,
    );
    setIsMassRejectionModalOpen(true);
  };

  const handleSendMassRejectionMail = () => {
    const ids = selectedRejectedIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    if (ids.length === 0) {
      toast.error("Please select applicants first.");
      return;
    }
    if (!String(massMailSubject || "").trim()) {
      toast.error("Please enter subject line.");
      return;
    }
    if (!String(massMailBody || "").trim()) {
      toast.error("Please enter body message.");
      return;
    }
    const jobId = Number(
      activeProjectId ||
      selectedProjectId ||
      selectedProject?.project_id ||
      selectedProject?.job_id,
    );
    if (!Number.isFinite(jobId)) {
      toast.error("Job ID is missing.");
      return;
    }
    dispatch(
      sendHireTalentMassRejectionEmail({
        payload: {
          job_id: jobId,
          applied_ids: ids,
          subject: String(massMailSubject || "").trim(),
          body: String(massMailBody || "").trim(),
        },
        callback: (response) => {
          if (response?.success || Number(response?.code) === 200) {
            setIsMassRejectionModalOpen(false);
            setSelectedRejectedIds([]);
            setIsSelectAllRejected(false);
            toast.success(response?.message || "Mass rejection email sent.");
          } else {
            toast.error(response?.message || "Unable to send mass rejection email.");
          }
        },
      }),
    );
  };

  const handleScheduleSubmit = () => {
    if (!activeApplicant?.applied_id) return;
    if (!scheduleData.date || !scheduleData.time) return;
    const scheduledDateTime = new Date(
      `${scheduleData.date}T${scheduleData.time}:00`,
    ).toISOString();
    dispatch(
      scheduleHireTalentInterview({
        params: activeApplicant.applied_id,
        payload: { scheduled_time: scheduledDateTime },
        callback: (response) => {
          loadApplications(activeProjectId || selectedProjectId, activeTab);
          if (response?.success) {
            setIsScheduleModalOpen(false);
            setActiveApplicant(null);
            setScheduleData({ date: "", time: "" });
          }
        },
      }),
    );
  };

  const handleRejectApplicant = () => {
    if (!activeApplicant?.applied_id) return;
    dispatch(
      rejectHireTalentApplicant({
        params: activeApplicant.applied_id,
        payload: { status: "rejected" },
        callback: (response) => {
          if (response?.success) {
            loadApplications(activeProjectId || selectedProjectId, activeTab);
            setActiveApplicant(null);
          }
        },
      }),
    );
  };

  const handleQuickApplicantStatusChange = (applicant, quickValue) => {
    if (!applicant?.applied_id) return;
    const apiAction = getApiActionFromQuickValue(quickValue);
    dispatch(
      updateHireTalentQuickApplicantStatus({
        params: applicant?.applied_id,
        payload: { action: apiAction },
        callback: (response) => {
          if (response?.success || Number(response?.code) === 200) {
            loadApplications(activeProjectId || selectedProjectId, activeTab);
          }
        },
      }),
    );
  };

  const handleQuickApplicantModalAction = (actionValue, successMessage) => {
    if (!activeApplicant?.applied_id) return;
    dispatch(
      updateHireTalentQuickApplicantStatus({
        params: activeApplicant?.applied_id,
        payload: { action: actionValue },
        callback: (response) => {
          if (response?.success || Number(response?.code) === 200) {
            loadApplications(activeProjectId || selectedProjectId, activeTab);
            setActiveApplicant(null);
            toast.success(successMessage || response?.message || "Status updated successfully.");
          } else {
            toast.error(response?.message || "Unable to update applicant status.");
          }
        },
      }),
    );
  };

  const handleOpenQuickContactModal = () => {
    setQuickContactApplicant(activeApplicant || null);
    setIsQuickContactModalOpen(true);
    setActiveApplicant(null);
  };

  const handleInviteQuickApplicantToNuvio = () => {
    if (!quickContactApplicant?.applied_id) return;
    dispatch(
      updateHireTalentQuickApplicantStatus({
        params: quickContactApplicant?.applied_id,
        payload: { action: "contact" },
        callback: (response) => {
          if (response?.success || Number(response?.code) === 200) {
            loadApplications(activeProjectId || selectedProjectId, activeTab);
            setIsQuickContactModalOpen(false);
            setQuickContactApplicant(null);
            setActiveApplicant(null);
            toast.success(response?.message || "Invite sent successfully.");
          } else {
            toast.error(response?.message || "Unable to update applicant status.");
          }
        },
      }),
    );
  };

  const handleQuickContactSupportRedirect = () => {
    setIsQuickContactModalOpen(false);
    setQuickContactApplicant(null);
    setActiveApplicant(null);
    navigate(coachPaths?.ContactUs, { state: { data: "landing" } });
  };

  return (
    <HireTalentLayout>
      {subLoading && <Loader />}
      <div className="ct_px_40 ct_mt_36 pb-5">
        <div className="row">
          <div class="col-lg-2 col-md-3 mb-4 mb-md-0">
            <h4 class="mb-0 ct_fs_20  mb-4">Matches</h4>
            <div class="ct_filter_btn "><button class="ct_outline_grey ct_yellow_btn ct_h_35 d-flex align-items-center gap-2">Filter<img alt="" src="../assets/img/octicon_filter-16.png" /></button></div>
            <div class="ct_filter_sidebar ct_custom_scroll p-0">
              <div class="ct_close_filter d-block d-lg-none"><i class="fa-solid fa-xmark"></i></div>
              <div class="mt-4">
                <h5 class="ct_fs_16 mb-3 ">Match Rating</h5>
                <ul>
                  <li>
                    <div class="form-check ct_custom_check2 ct_custom_check_grey d-flex align-items-center gap-2"><input class="form-check-input" id="flexCheckDefault" type="checkbox" value="" /><label for="" class="ct_text_clr_757886 ct_fs_14">90%-100%</label></div>
                  </li>
                  <li class="mt-2">
                    <div class="form-check ct_custom_check2 ct_custom_check_grey d-flex align-items-center gap-2"><input class="form-check-input" id="flexCheckDefault" type="checkbox" value="" /><label for="" class="ct_text_clr_757886 ct_fs_14">70%-80%</label></div>
                  </li>
                  <li class="mt-2">
                    <div class="form-check ct_custom_check2 ct_custom_check_grey d-flex align-items-center gap-2"><input class="form-check-input" id="flexCheckDefault" type="checkbox" value="" /><label for="" class="ct_text_clr_757886 ct_fs_14">50-70%</label></div>
                  </li>
                  <li class="mt-2">
                    <div class="form-check ct_custom_check2 ct_custom_check_grey d-flex align-items-center gap-2"><input class="form-check-input" id="flexCheckDefault" type="checkbox" value="" /><label for="" class="ct_text_clr_757886 ct_fs_14">{"<50%"}</label></div>
                  </li>

                </ul>
              </div>
              <div class="mt-4">
                <h5 class="ct_fs_16 mb-3 ">License</h5>
                <ul>

                  <li class="mt-2">
                    <div class="form-check ct_custom_check2 ct_custom_check_grey d-flex align-items-center gap-2"><input class="form-check-input" id="flexCheckDefault" type="checkbox" value="" /><label for="" class="ct_text_clr_757886 ct_fs_14">Yes</label></div>
                  </li>
                  <li class="mt-2">
                    <div class="form-check ct_custom_check2 ct_custom_check_grey d-flex align-items-center gap-2"><input class="form-check-input" id="flexCheckDefault" type="checkbox" value="" /><label for="" class="ct_text_clr_757886 ct_fs_14">No</label></div>
                  </li>
                </ul>
              </div>
              <div class="mt-4">
                <h5 class="ct_fs_16 mb-3 ">Experience</h5>
                <ul>
                  <li>
                    <div class="form-check ct_custom_check2 ct_custom_check_grey d-flex align-items-center gap-2"><input class="form-check-input" id="flexCheckDefault" type="checkbox" value="" /><label for="" class="ct_text_clr_757886 ct_fs_14">5-7 Years</label></div>
                  </li>
                  <li class="mt-2">
                    <div class="form-check ct_custom_check2 ct_custom_check_grey d-flex align-items-center gap-2"><input class="form-check-input" id="flexCheckDefault" type="checkbox" value="" /><label for="" class="ct_text_clr_757886 ct_fs_14">3-5 Years</label></div>
                  </li>
                  <li class="mt-2">
                    <div class="form-check ct_custom_check2 ct_custom_check_grey d-flex align-items-center gap-2"><input class="form-check-input" id="flexCheckDefault" type="checkbox" value="" /><label for="" class="ct_text_clr_757886 ct_fs_14">1-3 Years</label></div>
                  </li>

                </ul>
              </div>
              <div class="mt-4">
                <h5 class="ct_fs_16 mb-3 ">Location</h5>
                <ul>
                  <li>
                    <div class="form-check ct_custom_check2 ct_custom_check_grey d-flex align-items-center gap-2"><input class="form-check-input" id="flexCheckDefault" type="checkbox" value="" /><label for="" class="ct_text_clr_757886 ct_fs_14">Toronto, Canada</label></div>
                  </li>
                  <li class="mt-2">
                    <div class="form-check ct_custom_check2 ct_custom_check_grey d-flex align-items-center gap-2"><input class="form-check-input" id="flexCheckDefault" type="checkbox" value="" /><label for="" class="ct_text_clr_757886 ct_fs_14">Newyork, USA</label></div>
                  </li>
                  <li class="mt-2">
                    <div class="form-check ct_custom_check2 ct_custom_check_grey d-flex align-items-center gap-2"><input class="form-check-input" id="flexCheckDefault" type="checkbox" value="" /><label for="" class="ct_text_clr_757886 ct_fs_14">Lead, York</label></div>
                  </li>

                </ul>
              </div>
              <div class="mt-4">
                <h5 class="ct_fs_16 mb-3 ">Match Date</h5>
                <ul>
                  <li>
                    <div class="form-check ct_custom_check2 ct_custom_check_grey d-flex align-items-center gap-2"><input class="form-check-input" id="flexCheckDefault" type="checkbox" value="" /><label for="" class="ct_text_clr_757886 ct_fs_14">Today</label></div>
                  </li>
                  <li class="mt-2">
                    <div class="form-check ct_custom_check2 ct_custom_check_grey d-flex align-items-center gap-2"><input class="form-check-input" id="flexCheckDefault" type="checkbox" value="" /><label for="" class="ct_text_clr_757886 ct_fs_14">This Week</label></div>
                  </li>
                  <li class="mt-2">
                    <div class="form-check ct_custom_check2 ct_custom_check_grey d-flex align-items-center gap-2"><input class="form-check-input" id="flexCheckDefault" type="checkbox" value="" /><label for="" class="ct_text_clr_757886 ct_fs_14">This Month</label></div>
                  </li>

                </ul>
              </div>
            </div>
          </div>
          <div className=" col-lg-10 col-md-9 mb-4 mb-md-0">
            <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <div className="position-relative">
                  <select
                    className="form-control ct_input ct_h_48 py-2 h-auto pe-5 "
                    value={activeProjectId}
                    onChange={handleProjectChange}
                    style={{ minWidth: "280px" }}
                  >
                    {/* <option value="">Select Project</option> */}
                    {applicationProjects?.map((project) => (
                      <option key={project?.project_id} value={project?.project_id}>
                        {project?.project_title || "Untitled Project"}
                      </option>
                    ))}
                  </select>
                  <i className="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                </div>
                <span
                  className="ct_py_5_px_10 ct_border_radius_5 d-inline-block ct_fs_12"
                  style={{
                    backgroundColor: "#FFF5CC",
                    color: "#D89400",
                    lineHeight: "20px",
                  }}
                >
                  {selectedProject?.status || "Open"}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button className="ct_outline_btn px-3 py-2  ct_fs_12" type="button" style={{ height: "30px" }}>
                  <i className="fa-solid fa-paper-plane me-2"></i> Share
                </button>
                <button className="ct_outline_btn px-3 py-2  ct_fs_12" type="button" style={{ height: "30px" }}>
                  <i className="fa-regular fa-pen-to-square me-2"></i> Edit Post
                </button>
              </div>
            </div>
            <p className="ct_fs_14 mb-3 ct_mt_12 ct_darkblue_text">
              <i className="fa-solid fa-location-dot me-1"></i>
              {selectedProject?.location || "Location not specified"}
            </p>
            <div className="ct_outline_bg ct_box_shadow_card_1 ct_border_radius_10 p-4 mb-4">
              <div className="row g-3">
                <div className="col-lg-4 col-md-6">
                  <p className="mb-1 ct_fs_12 ct_grey_text">Category</p>
                  <p className="mb-0 ct_fs_14 ct_darkblue_text">{selectedProject?.category || "#N/A"}</p>
                </div>
                <div className="col-lg-4 col-md-6">
                  <p className="mb-1 ct_fs_12 ct_grey_text">Availability</p>
                  <p className="mb-0 ct_fs_14 ct_darkblue_text">{selectedProject?.availability || "#N/A"}</p>
                </div>
                <div className="col-lg-4 col-md-6">
                  <p className="mb-1 ct_fs_12 ct_grey_text">Work Approach</p>
                  <p className="mb-0 ct_fs_14 ct_darkblue_text">{selectedProject?.candidate_location || "#N/A"}</p>
                </div>
                <div className="col-lg-4 col-md-6">
                  <p className="mb-1 ct_fs_12 ct_grey_text">Experience</p>
                  <p className="mb-0 ct_fs_14 ct_darkblue_text">
                    {selectedProject?.years_of_experience ? `${selectedProject?.years_of_experience} Years` : "#N/A"}
                  </p>
                </div>
                <div className="col-lg-4 col-md-6">
                  <p className="mb-1 ct_fs_12 ct_grey_text">Salary</p>
                  <p className="mb-0 ct_fs_14 ct_darkblue_text">
                    {selectedProject?.salary_currency || ""} {selectedProject?.min_salary || "0"} - {selectedProject?.max_salary || "0"}
                  </p>
                </div>
                <div className="col-lg-4 col-md-6">
                  <p className="mb-1 ct_fs_12 ct_grey_text">License</p>
                  <p className="mb-0 ct_fs_14 ct_darkblue_text">{selectedProject?.license || "#N/A"}</p>
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center flex-wrap gap-3 mb-3 border-bottom pb-2">
              {dynamicTabs.map((tabKey) => (
                <button
                  key={tabKey}
                  type="button"
                  className="ct_fs_14 border-0 bg-transparent pb-1"
                  onClick={() => handleTabChange(tabKey)}
                  style={{
                    color: activeTab === tabKey ? "#219EBC" : "var(--dark_blue)",
                    borderBottom: activeTab === tabKey ? "2px solid var(--sky_blue)" : "2px solid transparent",
                    fontWeight: activeTab === tabKey ? 600 : 400,
                  }}
                >
                  {tabLabelMap[tabKey] || tabKey.replaceAll("_", " ")} ({applicationCounts?.[tabKey] || 0})
                </button>
              ))}
            </div>

            {isQuickApplyTab && (
              <div className="d-flex justify-content-end mb-3">
                <div className="position-relative" style={{ minWidth: "220px" }}>
                  <select
                    className="form-control ct_input ct_h_40 pe-5"
                    value={quickStatusFilter}
                    onChange={handleQuickStatusFilterChange}
                  >
                    {quickFilterOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <i className="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                </div>
              </div>
            )}

            {isRejectedFilterActive && (
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
                <label className="d-flex align-items-center gap-2 mb-0 ct_fs_14 ct_grey_text">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={isSelectAllRejected}
                    onChange={handleSelectAllRejected}
                  />
                  Select All
                </label>
                {selectedRejectedIds.length > 0 && (
                  <button
                    type="button"
                    className="ct_yellow_btn"
                    style={{ minWidth: "140px", height: "38px" }}
                    onClick={handleOpenMassRejectionModal}
                  >
                    Send a mail
                  </button>
                )}
              </div>
            )}


            <div className="table-responsive ct_custom_table ct_custom_table2 pt-2">
              <table className="table">
                <TableHeader data={tableHeaders} />
                <tbody>
                  {displayedApplications?.length > 0 &&
                    displayedApplications.map((item, index) => (
                      isQuickApplyTab ? (
                        <tr key={item?.applied_id || index}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <img
                                src={
                                  item?.applicant_profile_image
                                    ? `https://api.nu-vio.com/${item?.applicant_profile_image}`
                                    : "/assets/img/user_profile.png"
                                }
                                alt=""
                                className="ct_img_30"
                              />
                              <span className="ct_fw_600">
                                {item?.full_name || "#N/A"}
                              </span>
                            </div>
                          </td>
                          <td>{pipViewDate2(item?.created_at)}</td>
                          <td>{item?.job_location || item?.location || "#N/A"}</td>
                          <td>{item?.job_candidate_availability || item?.availability || item?.work_type || "#N/A"}</td>
                          <td>
                            {item?.job_years_of_experience
                              ? `${item?.job_years_of_experience} Years`
                              : item?.years_of_experience || "#N/A"}
                          </td>
                          <td className="text-capitalize">
                            {isQuickStatusLocked(item?.status) ? (
                              String(item?.status || "#N/A")
                            ) : (
                              <div className="position-relative">
                                <select
                                  className="form-control ct_input ct_fs_14 pe-4 text-capitalize"
                                  value={getQuickStatusValue(item?.status)}
                                  onChange={(event) =>
                                    handleQuickApplicantStatusChange(item, event.target.value)
                                  }
                                  disabled={subLoading}
                                  style={{ minWidth: "130px", height: "38px" }}
                                >
                                  {quickStatusOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                <i className="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                              </div>
                            )}
                          </td>
                          <td>
                            <button
                              className="ct_btn_apply_v2"
                              type="button"
                              onClick={() => setActiveApplicant(item)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ) : (
                        <tr key={item?.applied_id || index}>
                          <td>{index + 1}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <img
                                src={
                                  item?.applicant_profile_image
                                    ? `https://api.nu-vio.com/${item?.applicant_profile_image}`
                                    : "/assets/img/user_profile.png"
                                }
                                alt=""
                                className="ct_img_30"
                              />
                              <span className="ct_fw_600">
                                {item?.full_name || "#N/A"}
                              </span>
                            </div>
                          </td>
                          <td>{item?.job_title || "#N/A"}</td>
                          <td>{item?.email || "#N/A"}</td>
                          <td>{item?.phone_number || "#N/A"}</td>
                          <td>{item?.expected_salary || "#N/A"}</td>
                          <td>{item?.work_type || "#N/A"}</td>
                          <td className="text-capitalize">
                            {item?.status || "#N/A"}
                          </td>
                          <td>{pipViewDate2(item?.created_at)}</td>
                          <td>
                            <button
                              className="ct_btn_apply_v2"
                              type="button"
                              onClick={() => setActiveApplicant(item)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      )
                    ))}
                  {displayedApplications?.length === 0 && (
                    <tr>
                      <td colSpan={isQuickApplyTab ? 7 : 10} className="text-center ct_grey_text">
                        No applications found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>


          </div>

        </div>
      </div>

      {activeApplicant && (
        <div
          className="modal d-block ct_congratulation_modal_fade"
          tabIndex="-1"
        >
          <div
            className="modal-dialog modal-dialog-centered ct_modal-dialog"
            style={{ maxWidth: "820px" }}
          >
            <div className="modal-content ct_border_radius_20">
              <div className="modal-header border-0 align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <h4 className="ct_fs_20 mb-0 ct_darkblue_text">
                    Applicant Overview
                  </h4>
                  <p className="mb-0 ct_fs_14 ct_grey_text">
                    Direct Application - Not a NUVIO Profile
                  </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn-close ct_circle_close"
                    onClick={() => {
                      setActiveApplicant(null);
                      setIsQuickContactModalOpen(false);
                    }}
                  ></button>
                </div>
              </div>
              <div className="modal-body pt-0">
                {false && (<form className="ct_modal_scroll ct_custom_scroll px-4 pb-3">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          className="form-control ct_input"
                          value={activeApplicant?.full_name || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Email Address
                        </label>
                        <input
                          type="text"
                          className="form-control ct_input"
                          value={activeApplicant?.email || ""}
                          readOnly
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Phone Number
                        </label>
                        <PhoneInput
                          id="phone_number"
                          className="ct_phone_input"
                          defaultCountry="MU"
                          value={activeApplicant?.phone_number || ""}
                          onChange={noop}
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Expected Salary (in local currency)
                        </label>
                        <input
                          type="text"
                          className="form-control ct_input"
                          value={activeApplicant?.expected_salary || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Work
                        </label>
                        <div className="d-flex flex-column gap-2 position-relative">
                          <select
                            className="form-control ct_input"
                            value={activeApplicant?.work_type || ""}
                            disabled
                          >
                            <option value="">Select Work</option>
                            {workOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <i className="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Current Position (if unemployed, then “Unemployed”)
                        </label>
                        <input
                          type="text"
                          className="form-control ct_input"
                          value={activeApplicant?.current_position || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Skills
                        </label>
                        <div className="form-control ct_input h-auto py-2 d-flex align-items-center gap-2 flex-wrap">
                          {applicantSkills?.length > 0 ? (
                            applicantSkills.map((skill, index) => (
                              <span key={`${skill}-${index}`} className="ct_skill_badge">
                                {skill}
                              </span>
                            ))
                          ) : (
                            <span className="ct_grey_text">#N/A</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Linkedin Profile Link
                        </label>
                        <input
                          type="text"
                          className="form-control ct_input"
                          value={applicantLinkedinUrl || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Porfolio Link
                        </label>
                        <input
                          type="text"
                          className="form-control ct_input"
                          value={applicantPortfolioUrl || ""}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Cover Letter
                        </label>
                        <textarea
                          className="form-control ct_input h-auto"
                          rows={4}
                          value={activeApplicant?.cover_letter || ""}
                          readOnly
                        ></textarea>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Upload Resume
                        </label>
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                          <input
                            type="text"
                            className="form-control ct_input"
                            value={activeApplicant?.resume_url || ""}
                            readOnly
                          />
                          {activeApplicant?.resume_url && (
                            <a
                              href={activeApplicant?.resume_url}
                              target="_blank"
                              rel="noreferrer"
                              className="ct_outline_btn px-3 py-2 ct_fs_12"
                              style={{ height: "38px" }}
                            >
                              View Resume
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </form>)}
                <div className="ct_modal_scroll ct_custom_scroll px-4 pb-3">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="ct_outline_bg ct_border_radius_10 p-3 h-100" style={{ border: "1px solid #D9DEE8" }}>
                        <h6 className="ct_fs_18 ct_darkblue_text mb-3">Information</h6>
                        <div className="d-flex justify-content-between gap-2 mb-3">
                          <span className="ct_fs_14 ct_grey_text">Name</span>
                          <span className="ct_fs_14 ct_darkblue_text">{applicantFirstName}</span>
                        </div>
                        <div className="d-flex justify-content-between gap-2 mb-3">
                          <span className="ct_fs_14 ct_grey_text">Last Name</span>
                          <span className="ct_fs_14 ct_darkblue_text">{applicantLastName}</span>
                        </div>
                        <div className="d-flex justify-content-between gap-2 mb-3">
                          <span className="ct_fs_14 ct_grey_text">Email</span>
                          <span className="ct_fs_14 ct_darkblue_text text-break text-end">{activeApplicant?.email || "#N/A"}</span>
                        </div>
                        <div className="d-flex justify-content-between gap-2">
                          <span className="ct_fs_14 ct_grey_text">Phone</span>
                          <span className="ct_fs_14 ct_darkblue_text">{activeApplicant?.phone_number || "#N/A"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="ct_outline_bg ct_border_radius_10 p-3 h-100" style={{ border: "1px solid #D9DEE8" }}>
                        <h6 className="ct_fs_18 ct_darkblue_text mb-3">Location</h6>
                        <div className="d-flex justify-content-between gap-2 mb-3">
                          <span className="ct_fs_14 ct_grey_text">Location</span>
                          <span className="ct_fs_14 ct_darkblue_text">{applicantLocation}</span>
                        </div>
                        <div className="d-flex justify-content-between gap-2 mb-3">
                          <span className="ct_fs_14 ct_grey_text">Availability</span>
                          <span className="ct_fs_14 ct_darkblue_text text-end">
                            {activeApplicant?.job_candidate_availability || activeApplicant?.availability || activeApplicant?.work_type || "#N/A"}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between gap-2 mb-3">
                          <span className="ct_fs_14 ct_grey_text">Experience</span>
                          <span className="ct_fs_14 ct_darkblue_text">{applicantExperience}</span>
                        </div>
                        <div className="d-flex justify-content-between gap-2 mb-3">
                          <span className="ct_fs_14 ct_grey_text">LinkedIn Profile</span>
                          {applicantLinkedinUrl ? (
                            <a href={applicantLinkedinUrl} target="_blank" rel="noreferrer" className="ct_fs_14 ct_link_blue text-end">Link</a>
                          ) : (
                            <span className="ct_fs_14 ct_darkblue_text">#N/A</span>
                          )}
                        </div>
                        <div className="d-flex justify-content-between gap-2">
                          <span className="ct_fs_14 ct_grey_text">Application Date</span>
                          <span className="ct_fs_14 ct_darkblue_text">
                            {pipViewDate2(activeApplicant?.created_at || activeApplicant?.applied_at) || "#N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="ct_outline_bg ct_border_radius_10 p-3 h-100" style={{ border: "1px solid #D9DEE8", minHeight: "180px" }}>
                        <h6 className="ct_fs_18 ct_darkblue_text mb-3">Skills</h6>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          {applicantSkills?.length > 0 ? (
                            applicantSkills.map((skill, index) => (
                              <span key={`${skill}-${index}`} className="ct_skill_badge">
                                {skill}
                              </span>
                            ))
                          ) : (
                            <span className="ct_grey_text">#N/A</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="ct_outline_bg ct_border_radius_10 p-3 h-100" style={{ border: "1px solid #D9DEE8", minHeight: "180px" }}>
                        <h6 className="ct_fs_18 ct_darkblue_text mb-3">Application materials</h6>
                        <div className="d-flex justify-content-between gap-2 mb-3">
                          <span className="ct_fs_14 ct_grey_text">Cover letter</span>
                          <span className="ct_fs_14 ct_darkblue_text text-end">{activeApplicant?.cover_letter || "#N/A"}</span>
                        </div>
                        <div className="d-flex justify-content-between gap-2 mb-3">
                          <span className="ct_fs_14 ct_grey_text">CV</span>
                          {activeApplicant?.resume_url ? (
                            <a
                              href={activeApplicant?.resume_url}
                              target="_blank"
                              rel="noreferrer"
                              className="ct_fs_14 ct_link_blue text-end"
                            >
                              View CV
                            </a>
                          ) : (
                            <span className="ct_fs_14 ct_darkblue_text">#N/A</span>
                          )}
                        </div>
                        <div className="d-flex justify-content-between gap-2">
                          <span className="ct_fs_14 ct_grey_text">Portfolio Link</span>
                          {applicantPortfolioUrl ? (
                            <a href={applicantPortfolioUrl} target="_blank" rel="noreferrer" className="ct_fs_14 ct_link_blue text-end">Link</a>
                          ) : (
                            <span className="ct_fs_14 ct_darkblue_text">#N/A</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-center flex-wrap gap-3 mt-4">
                  {isActiveApplicantQuickApply ? (
                    <>
                      {isQuickApplicantNewInModal ? (
                        <>
                          <button
                            className="ct_outline_btn px-5"
                            type="button"
                            style={{ height: "42px", borderRadius: "22px", minWidth: "140px" }}
                            onClick={() =>
                              handleQuickApplicantModalAction("shortlist", "Applicant shortlisted successfully.")
                            }
                          >
                            Shortlist
                          </button>
                          <button
                            className="ct_yellow_btn ct_sky_blue_btn px-5"
                            type="button"
                            style={{ height: "42px", borderRadius: "22px", minWidth: "140px" }}
                            onClick={handleOpenQuickContactModal}
                          >
                            Contact
                          </button>
                        </>
                      ) : (
                        <span className="ct_darkblue_text ct_fw_600 ct_fs_16">
                          {getQuickStatusLabel(activeApplicant?.status)}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      {!activeApplicant?.scheduled_time ? (
                        <button
                          className="ct_outline_btn px-5"
                          type="button"
                          style={{ height: "42px", borderRadius: "22px", minWidth: "140px" }}
                          onClick={() => setIsScheduleModalOpen(true)}
                        >
                          Shortlist
                        </button>
                      ) : ["accepted", "rejected"].includes(
                        String(
                          activeApplicant?.schedule_status || "",
                        ).toLowerCase(),
                      ) ? (
                        <button
                          className={`ct_yellow_btn px-4 ${String(
                            activeApplicant?.schedule_status || "",
                          ).toLowerCase() === "accepted"
                            ? "ct_sky_blue_btn"
                            : "ct_orange_btn"
                            }`}
                          type="button"
                          style={{ height: "42px", borderRadius: "22px", minWidth: "140px" }}
                          disabled
                        >
                          {String(activeApplicant?.schedule_status || "")}
                        </button>
                      ) : (
                        <button
                          className="ct_yellow_btn ct_sky_blue_btn px-4"
                          type="button"
                          style={{ height: "42px", borderRadius: "22px", minWidth: "140px" }}
                          disabled
                        >
                          Contacted
                        </button>
                      )}
                    </>
                  )}
                  {!isActiveApplicantQuickApply && String(
                    activeApplicant?.schedule_status || "",
                  ).toLowerCase() !== "rejected" && (
                      <button
                        className="ct_yellow_btn ct_orange_btn px-5"
                        type="button"
                        style={{ height: "42px", borderRadius: "22px", minWidth: "140px" }}
                        onClick={handleRejectApplicant}
                      >
                        Reject Applicant
                      </button>
                    )}
                </div>
                <div className="mt-4"></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isQuickContactModalOpen && (
        <div className="modal d-block ct_congratulation_modal_fade" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered ct_modal-dialog" style={{ maxWidth: "560px" }}>
            <div className="modal-content ct_border_radius_20">
              <div className="modal-header border-0 pb-0">
                <h5 className="ct_fs_20 ct_darkblue_text mb-0">Book a call with Quick Applicant</h5>
                <button
                  type="button"
                  className="btn-close ct_circle_close"
                  onClick={() => {
                    setIsQuickContactModalOpen(false);
                    setQuickContactApplicant(null);
                  }}
                ></button>
              </div>
              <div className="modal-body text-center px-4 pb-4">
                <div className="mb-3">
                  <span
                    className="d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: "48px",
                      height: "48px",
                      backgroundColor: "#FF9800",
                      borderRadius: "10px",
                      color: "#fff",
                      fontSize: "24px",
                      fontWeight: 700,
                    }}
                  >
                    !
                  </span>
                </div>
                <h4 className="ct_fs_24 ct_darkblue_text mb-2">You&apos;ve found a potential collaborator!</h4>
                <p className="ct_fs_16 ct_grey_text mb-4">
                  As they don&apos;t have a NUVIO profile, please contact them directly to schedule an interview.
                </p>
                <button
                  className="ct_yellow_btn ct_sky_blue_btn w-100 mb-2"
                  type="button"
                  style={{ height: "46px", borderRadius: "24px" }}
                  onClick={handleInviteQuickApplicantToNuvio}
                >
                  Invite to NUVIO Profile
                </button>
                <p className="mb-0 ct_fs_15 ct_grey_text">
                  Need help?{" "}
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0 ct_orange_text ct_fw_600"
                    onClick={handleQuickContactSupportRedirect}
                  >
                    Contact Support
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {isMassRejectionModalOpen && (
        <div className="modal d-block ct_congratulation_modal_fade" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered ct_modal-dialog" style={{ maxWidth: "760px" }}>
            <div className="modal-content ct_border_radius_20">
              <div className="modal-header border-0 align-items-center">
                <h4 className="ct_fs_22 mb-0 ct_darkblue_text">Mass Rejection Email</h4>
                <button
                  type="button"
                  className="btn-close ct_circle_close"
                  onClick={() => setIsMassRejectionModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body pt-0">
                <div className="px-4 pb-4">
                  <p className="ct_orange_text ct_fs_14 mb-3">
                    Streamline your hiring and communicate professionally with all applicants at once.
                  </p>
                  <p className="ct_fs_16 mb-1 ct_grey_text">
                    Job Title: <span className="ct_darkblue_text">
                      {currentProjectTitle}
                    </span>
                  </p>
                  <p className="ct_fs_16 mb-1 ct_grey_text">
                    Company Name: <span className="ct_darkblue_text">
                      {currentCompanyName}
                    </span>
                  </p>
                  <p className="ct_fs_16 mb-4 ct_grey_text">
                    Applicants: <span className="ct_darkblue_text">
                      {selectedRejectedIds.length}
                    </span>
                  </p>

                  <h5 className="ct_fs_20 ct_darkblue_text mb-3">Compose your email</h5>
                  <div className="form-group mb-3">
                    <label className="ct_orange_text ct_fs_14 mb-2">Subject Line:</label>
                    <input
                      type="text"
                      className="form-control ct_input"
                      value={massMailSubject}
                      onChange={(event) => setMassMailSubject(event.target.value)}
                    />
                  </div>
                  <div className="form-group mb-4">
                    <label className="ct_orange_text ct_fs_14 mb-2">Body / Message:</label>
                    <textarea
                      className="form-control ct_input h-auto"
                      rows={8}
                      value={massMailBody}
                      onChange={(event) => setMassMailBody(event.target.value)}
                    ></textarea>
                  </div>
                  <div className="d-flex justify-content-center">
                    <button
                      className="ct_yellow_btn"
                      type="button"
                      style={{ minWidth: "140px", height: "42px" }}
                      onClick={handleSendMassRejectionMail}
                    >
                      Send
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isScheduleModalOpen && (
        <div
          className="modal d-block ct_congratulation_modal_fade"
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content ct_border_radius_20">
              <div className="modal-header border-0 align-items-center">
                <h4 className="ct_fs_18 mb-0 ct_darkblue_text">
                  Schedule Interview
                </h4>
                <button
                  type="button"
                  className="btn-close ct_circle_close"
                  onClick={() => setIsScheduleModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body pt-0">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="mb-2 ct_fs_14 ct_grey_text">Date</label>
                      <input
                        type="date"
                        name="date"
                        value={scheduleData.date}
                        onChange={handleScheduleChange}
                        min={new Date().toISOString().split("T")[0]}
                        className="form-control ct_input_border_8"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="mb-2 ct_fs_14 ct_grey_text">Time</label>
                      <input
                        type="time"
                        name="time"
                        value={scheduleData.time}
                        onChange={handleScheduleChange}
                        min={getMinTimeForSelectedDate()}
                        className="form-control ct_input_border_8"
                      />
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-end gap-3 mt-3">
                  <button
                    className="ct_yellow_btn ct_outline_grey px-4"
                    type="button"
                    onClick={() => setIsScheduleModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="ct_yellow_btn px-4"
                    type="button"
                    onClick={handleScheduleSubmit}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </HireTalentLayout>
  );
};

export default Applicants;
